import { type SiteId, SiteStatus, SiteVisibilityStatus } from '@solinkcorp/api-types';
import { Flex, useInterval, useWindowSize } from '@solinkcorp/solink-components';
import { useQueryParams } from '@solinkcorp/utils/hooks';
import EventActions from 'actions/EventActions';
import classNames from 'classnames';
import PublicApi from 'clients/PublicApi';
import {
  getDetectionMotionTime,
  getDetectionSeekTime,
  getEarliestValidDetection,
  VERIFEYE_OFFSET,
} from 'components/AlarmsPage/utils/alarmUtils';
import PlayerCameraSlidePicker from 'components/PlayerCameraSlidePicker/PlayerCameraSlidePicker';
import PublicWrapper from 'components/PublicPages/PublicWrapper';
import {
  type CameraIdentifier,
  PlayingCamerasController,
} from 'components/VideoPlayer/controller/PlayingCamerasController';
import usePublicPlaybackController from 'components/VideoPlayer/controller/usePublicPlaybackController';
import { VideoPlayerOptionsController } from 'components/VideoPlayer/controller/VideoPlayerOptionsController';
import PublicVideoPlayer from 'components/VideoPlayer/PublicVideoPlayer';
import Hls from 'hls.js';
import isMobile from 'ismobilejs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useDimensions from 'react-cool-dimensions';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router-dom';
import { useControllerState, useLocalController } from 'solox';
import useLocale from 'src/hooks/useLocale';
import useSWR from 'swr';
import { type CallhomeCamera } from 'utils/CameraUtil';
import isShallowEqual from 'utils/isShallowEqual';
import { type CamerasDictionary, type VideoDiscoverySite } from 'utils/types';
import { THUMBNAIL_TYPE_PUBLIC_CAMERA } from 'vd-constants';
import '../../../styles/main.sass';
import { Button } from '../Button';
import { Error } from '../Error';
import { Footer } from '../Footer';
import { Header } from '../Header';
import { SubHeader } from '../SubHeader';
import usePlayingCamsPlaylistStartAndEnd from '../usePlayingCamsPlaylistStartAndEnd';
import {
  addQueryParamToURL,
  checkTokenExpiration,
  constructAlarmEventURL,
  filterVideoLinkItems,
  hasLiveExpired,
} from '../utils/helperFunctions';
import './Alarm.sass';
import { AlarmDetails } from './AlarmDetails';

const isMobileDevice = isMobile(navigator.userAgent).phone;
// Component
const Alarm: React.FC = () => {
  /* Set the page title */
  document.title = 'Solink Alarm';

  const locale = useLocale();
  const params = useParams();
  /* Extract the alarm id from the url */
  const alarmId = params.alarmId || '';
  const [query] = useQueryParams();
  const cameraId = query.get('cameraId') || '';

  const [pollingInt, setPollingInt] = useState(5000);

  const [camerasById, setCamerasById] = useState<CamerasDictionary>({});
  const [sitesById, setSitesById] = useState<{ [siteId: SiteId]: VideoDiscoverySite }>({});
  const [devicesById, setDevicesById] = useState<Record<string, any>>({});
  const playerRef = useRef<HTMLDivElement | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);

  const playingCamerasController = useLocalController(() => new PlayingCamerasController(false));
  const { playingCameraIds, playingCameras } = useControllerState(
    playingCamerasController.state,
    (state) => ({
      playingCameraIds: state.playingCameraIds,
      playingCameras: playingCamerasController.getPlayingCameras(),
    }),
    isShallowEqual,
  );

  const updateCamerasById = (alarmEvent: any, locationName: string) => {
    const newCamerasById = alarmEvent.cameras.reduce((acc: any, cam: any) => {
      // Use closed playlists because open ones have lots of issues with WebVTT
      const url = new URL(cam.videoUrl);
      url.searchParams.delete('end');
      url.searchParams.delete('vtt');
      const videoUrl = addQueryParamToURL(url.toString(), 'closed', '1');

      const liveUrl = cam.videoUrl.replace('alarm.m3u8', 'live.m3u8');

      acc[cam.chId] = {
        ...cam,
        location: { name: locationName },
        audioParams: {},
        streams: cam.streams.map((stream: any) => ({
          ...stream,
          // initialize the earliestSegmentDate to 1
          // This ensures there is a stream available for the video player to play intially
          // Once the playlist is loaded we will update this value
          earliestSegmentDate: 1,
        })),
        videoUrl,
        liveUrl,
        deviceId: alarmEvent.locationId,
      };
      return acc;
    }, {});

    setCamerasById(newCamerasById);
  };

  const onSuccessAlarmEvent = ({
    alarmEvent: newAlarmEvent,
    device,
  }: {
    alarmEvent: any;
    device: any;
  }) => {
    // Stop polling if the alarm event is closed
    if (newAlarmEvent.state === 'CLOSED') {
      setPollingInt(0);
    }

    const targetCamera = newAlarmEvent.cameras.find((cam: any) => cam.id === cameraId);

    const playingCamera = targetCamera ?? newAlarmEvent.cameras[0];

    const newPlayingCameraIds: CameraIdentifier[] = [
      {
        locationId: newAlarmEvent.siteId, // TODO: remove after complete siteId migration
        siteId: newAlarmEvent.siteId,
        chCameraId: playingCamera.chId,
        vmsCameraId: playingCamera.id,
        playerId: `${playingCamera.id}-${newAlarmEvent.locationId}`, // locationId in the event from PublicAPI refers specifically to the deviceId of the primary camera included in that alarm event, ie: this will be the secondary deviceId if the primary camera is on the secondary device
      },
    ];

    updateCamerasById(newAlarmEvent, device?.name);
    // Create a minimal VideoDiscoverySite object for public alarm pages
    const minimalLocation: VideoDiscoverySite = {
      id: newAlarmEvent.siteId,
      cloudId: '' as any, // Not available in public context
      resellerId: '' as any, // Not available in public context
      customerId: newAlarmEvent.customerId,
      name: device?.name || 'Unknown Location', // TODO: change this to the site name when available in the API
      timezone: device?.timezoneId || 'UTC',
      status: SiteStatus.ACTIVE,
      visibilityStatus: SiteVisibilityStatus.VISIBLE,
      cameras: newAlarmEvent.cameras,
      alarmConfig: {} as any, // Not needed for public playback
      alarmContacts: [],
      cameraLinks: [],
      lastUpdateTime: new Date(),
      primaryDeviceId: newAlarmEvent.siteId,
    };
    setSitesById({ [newAlarmEvent.siteId]: minimalLocation });
    setDevicesById({ [newAlarmEvent.locationId]: {} }); // locationId in the event from PublicAPI refers specifically to the deviceId of the primary camera included in that alarm event, ie: this will be the secondary deviceId if the primary camera is on the secondary device

    // Only update playing camera ids if there aren't any
    if (!playingCameraIds.length) {
      playingCamerasController.setPlayingCameras(newPlayingCameraIds);
    }
  };
  const {
    data,
    error: swrError,
    isLoading,
  } = useSWR('publicAlarm', () => PublicApi.getAlarmEvent(alarmId), {
    refreshInterval: pollingInt,
    onSuccess: onSuccessAlarmEvent,
  });

  const { note, device, eventUrl, tokenExpires } = data || {};
  const alarmEvent = useMemo(() => data?.alarmEvent, [data]);
  const dataReady = !isLoading;

  const cameras = useMemo(
    () =>
      alarmEvent?.cameras.map((cam: any) => ({
        ...cam,
        cameraId: cam.id,
        id: cam.chId,
        deviceId: alarmEvent.locationId,
        siteId: alarmEvent.siteId,
      })) ?? [],
    [alarmEvent],
  );

  const tokenExpired = tokenExpires ? checkTokenExpiration(tokenExpires) : false;

  const error = tokenExpired
    ? locale.spa.videoPlaybackHasExpired
    : swrError
      ? locale.spa.requestedResourceIsUnavailable
      : undefined;

  const videoLinkItems = useMemo(
    () => filterVideoLinkItems(alarmEvent?.videoLinkItems || []),
    [alarmEvent?.videoLinkItems],
  );

  const videoExpirationDate = data?.tokenExpires ?? '';

  const videoPlayerOptionsController = useLocalController(
    () => new VideoPlayerOptionsController(false),
  );

  const filterDate = useMemo(
    () => (alarmEvent?.startTime ? new Date(alarmEvent.startTime).valueOf() : new Date().valueOf()),
    [alarmEvent],
  );

  // Earliest detection time across the alarm's video link items. Matches
  // VerifEye's "first motion" notion; used to start the timeline 2s before.
  const firstDetectionTimestamp = useMemo(() => {
    const detection = getEarliestValidDetection(videoLinkItems);
    return detection ? getDetectionMotionTime(detection) : undefined;
  }, [videoLinkItems]);

  const playbackStartTime = useMemo(
    () =>
      firstDetectionTimestamp !== undefined
        ? firstDetectionTimestamp - VERIFEYE_OFFSET
        : filterDate,
    [firstDetectionTimestamp, filterDate],
  );

  const playbackController = usePublicPlaybackController(
    playingCameras,
    playbackStartTime,
    { vtt: true },
    undefined,
    undefined,
    device?.timezoneId || 'UTC',
  );

  const { isPlayingLive, desiredTime } = useControllerState(
    playbackController.state,
    (state) => {
      return {
        isPlayingLive: playbackController.isPlayingLive(),
        desiredTime: state.desiredTime,
      };
    },
    isShallowEqual,
  );

  const { start, end } = usePlayingCamsPlaylistStartAndEnd(playingCameraIds, playbackController);

  // Cap the playable start so footage is only shown from VERIFEYE_OFFSET ms
  // before the first detection. If the playlist start is later (less footage
  // available), use that instead.
  const cappedStart = useMemo(() => {
    if (start === undefined) return undefined;
    if (firstDetectionTimestamp === undefined) return start;
    return Math.max(start, firstDetectionTimestamp - VERIFEYE_OFFSET);
  }, [start, firstDetectionTimestamp]);

  useEffect(() => {
    if (cappedStart !== undefined) {
      playbackController.updateVideoPlaybackStartsAtDate(new Date(cappedStart));
    }
    if (end) playbackController.updateVideoPlaybackEndsAtDate(new Date(end));
  }, [cappedStart, end, playbackController]);

  const camerasByIdWithSegmentDate = useMemo(() => {
    if (cappedStart === undefined) return camerasById;
    return Object.keys(camerasById).reduce((acc: CamerasDictionary, key) => {
      const camera = camerasById[key];
      acc[key] = {
        ...camera,
        streams: camera.streams?.map((stream: any) => ({
          ...stream,
          earliestSegmentDate: cappedStart,
        })),
      };
      return acc;
    }, {});
  }, [camerasById, cappedStart]);

  useEffect(() => {
    playbackController.seekToTime(playbackStartTime);
  }, [playbackStartTime, playbackController]);

  // Once the playlist loads, footage may begin after the VerifEye pre-roll.
  // Re-seek forward so playback is not left before available segments.
  useEffect(() => {
    if (cappedStart === undefined || cappedStart <= playbackStartTime) return;
    if (desiredTime < cappedStart) {
      playbackController.seekToTime(cappedStart);
    }
  }, [cappedStart, playbackStartTime, desiredTime, playbackController]);

  const appClass = classNames('public-app', {
    loading: !dataReady,
    'data-ready': dataReady,
    error: tokenExpired || swrError,
  });

  const windowSize = useWindowSize();

  const { width: playerWidth, height: playerHeight, observe } = useDimensions();

  const setPlayerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      observe(node);
      playerRef.current = node;
    },
    [observe],
  );

  const availableSpace = { width: playerWidth, height: playerHeight };

  const isOpenAlarm = alarmEvent?.state === 'OPEN';

  React.useEffect(() => {
    if (isOpenAlarm) {
      setEndTime(new Date());
    } else {
      setEndTime(
        new Date(videoLinkItems[videoLinkItems.length - 1]?.timestamp ?? alarmEvent?.endTime),
      );
    }
  }, [alarmEvent, isOpenAlarm, videoLinkItems]);

  useInterval(
    () => {
      setEndTime(new Date());
    },
    isOpenAlarm ? 1000 : undefined,
    [alarmEvent],
  );

  const startTime = React.useMemo(() => new Date(alarmEvent?.startTime), [alarmEvent]);

  const timelineEvent = React.useMemo(() => {
    return {
      ...alarmEvent,
      videoLinkItems,
      startTime,
      endTime,
    };
  }, [alarmEvent, videoLinkItems, startTime, endTime]);

  const timelineEvents = React.useMemo(() => [timelineEvent], [timelineEvent]);

  const playerType = 'publicAlarm';

  let player = (
    <Skeleton className="skeleton-player" baseColor="#1E1E1E" highlightColor="#1F1F1F" />
  );

  const liveExpired = hasLiveExpired(alarmEvent?.startTime);

  const inProgressEvents = React.useMemo(() => {
    if (
      endTime &&
      startTime &&
      desiredTime > startTime.valueOf() &&
      (desiredTime < endTime.valueOf() || isOpenAlarm)
    ) {
      return [timelineEvent];
    }
    return [];
  }, [desiredTime, startTime, endTime, timelineEvent, isOpenAlarm]);

  // resolve cameras by `vmsCameraId` for the SPA
  const selectedLocation = React.useMemo(() => {
    if (!alarmEvent?.siteId) return undefined;
    const site = sitesById[alarmEvent.siteId];
    if (!site) return undefined;
    return { ...site, cameras };
  }, [alarmEvent, sitesById, cameras]);

  if (dataReady) {
    player = (
      <PublicVideoPlayer
        availableSpace={availableSpace}
        windowSize={windowSize}
        playerType={playerType}
        videoPlayerOptionsController={videoPlayerOptionsController}
        playingCamerasController={playingCamerasController}
        timezone={device?.timezoneId || ''}
        filterDate={filterDate}
        playbackController={playbackController}
        sitesById={sitesById}
        camerasById={camerasByIdWithSegmentDate}
        liveHidden={liveExpired}
        isMobile={isMobileDevice}
        hideMiniMap={true}
        timelineEvents={timelineEvents}
        pinnedEvent={timelineEvent}
        inProgressEvents={inProgressEvents}
        devicesById={devicesById}
        selectedLocation={selectedLocation}
      />
    );
  }

  if (dataReady && error) {
    player = <div className="player-fake"></div>;
  }

  const status = alarmEvent?.state;

  const scrollToTopOfPlayer = () => {
    playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const updateCamerasByIdVideoUrl = ({ videoUrl, camId }: { videoUrl: string; camId: string }) => {
    const newCamerasById = {
      ...camerasById,
      [camId]: { ...camerasById[camId], videoUrl: addQueryParamToURL(videoUrl, 'closed', '1') },
    };
    setCamerasById(newCamerasById);
  };

  const hlsSupported = Hls.isSupported();

  const handleVideoLinkItemClick = (id: string, timestamp: number, videoUrl: string) => {
    const targetCam = cameras.find((cam: any) => cam.cameraId === id);

    if (targetCam) {
      // Don't update the playingCameras if the current camera is already playing and there is only one
      // Always update the playingCameras if hls is not supported because we rely on the startPlayback query param in the videoUrl to seek to the correct time
      if (
        playingCameraIds.length > 1 ||
        playingCameraIds[0].chCameraId !== targetCam.id ||
        !hlsSupported
      ) {
        const newCams: CameraIdentifier[] = [
          {
            locationId: targetCam.siteId, // TODO: remove after complete siteId migration
            siteId: targetCam.siteId,
            chCameraId: targetCam.id,
            vmsCameraId: targetCam.cameraId,
            playerId: `${targetCam.id}-${targetCam.deviceId}`,
          },
        ];
        playingCamerasController.setPlayingCameras(newCams);
      }

      // Seek to proper time
      // startPlayback query param is used if hls.js is not supported
      if (hlsSupported) {
        playbackController.seekToTime(timestamp);
      } else {
        playbackController.toggleLive(false);
        updateCamerasByIdVideoUrl({ videoUrl, camId: targetCam.id });
      }
      scrollToTopOfPlayer();
    } else {
      // eslint-disable-next-line
      // @ts-ignore
      throw new Error('Cannot find the associated camera');
    }
  };

  const onUpdatePlayerCamera = (id: string) => {
    if (hlsSupported) return;
    const targetCam = cameras.find((cam: any) => cam.id === id);
    // This ensures we are resetting the startPlayback to the first motion event for that camera.
    updateCamerasByIdVideoUrl({ videoUrl: targetCam.videoUrl, camId: id });
  };

  // When the user picks a camera from the slide picker, switch to it and seek
  // to that camera's first valid detection. Falls back to the alarm-level
  // playbackStartTime if the camera has no detection metadata.
  const playCameraFromPicker = (camera: CallhomeCamera) => {
    EventActions.clearActiveEvent();
    playingCamerasController.setPlayingCamera(camera);
    const detection = getEarliestValidDetection(videoLinkItems, camera.cameraId);
    const seekTime = detection
      ? (getDetectionSeekTime(detection) ?? playbackStartTime)
      : playbackStartTime;
    const effectiveSeekTime =
      cappedStart !== undefined ? Math.max(seekTime, cappedStart) : seekTime;
    playbackController.seekToTime(effectiveSeekTime);
  };

  const shouldManageAlarm = status === 'OPEN' && !tokenExpired;

  const vdLinkClass = classNames('vd-link', { 'manage-alarm': shouldManageAlarm });

  return (
    <Flex className={appClass} alignItems="center" flexDirection="column">
      <Flex className="container" alignItems="center" flexDirection="column">
        <Header event={alarmEvent} alarmPage={true} dataReady={dataReady} error={error} />
        <SubHeader
          event={alarmEvent}
          eventUrl={eventUrl}
          status={status}
          tokenExpired={tokenExpired}
          device={device}
          alarmPage={true}
          displayLiveButton={isMobileDevice && !liveExpired}
          isPlayingLive={isPlayingLive}
          toggleLive={playbackController.toggleLive}
          error={error}
        />
        <div className="main-content">
          <div className={classNames('player-container', { mobile: isMobileDevice })}>
            {error && (
              <Error>
                <span data-testid="error-message">{error}</span>
                {error === locale.spa.videoPlaybackHasExpired && (
                  <>
                    {'  '}
                    <a className={vdLinkClass} href={constructAlarmEventURL(eventUrl)}>
                      {shouldManageAlarm ? locale.spa.manageAlarm : locale.spa.openInApp}
                    </a>
                    {'  '}
                    <span>
                      {shouldManageAlarm ? locale.spa.manageAlarmInfo : locale.spa.openInAppInfo}
                    </span>
                  </>
                )}
              </Error>
            )}
            <div className="player" ref={setPlayerRef}>
              {player}
            </div>
            {!error && dataReady && (
              <PlayerCameraSlidePicker
                cameras={cameras}
                playingCamerasController={playingCamerasController}
                cameraThumbnailType={THUMBNAIL_TYPE_PUBLIC_CAMERA}
                multiPlayDisabled={isMobileDevice}
                onUpdatePlayerCamera={onUpdatePlayerCamera}
                playCamera={playCameraFromPicker}
              />
            )}
          </div>
          <AlarmDetails
            alarmEvent={alarmEvent}
            error={error}
            dataReady={dataReady}
            tokenExpired={tokenExpired}
            videoExpirationDate={videoExpirationDate}
            device={device}
            note={note}
            handleVideoLinkItemClick={handleVideoLinkItemClick}
            videoLinkItems={videoLinkItems}
          />
        </div>
        <Footer status={status} tokenExpired={tokenExpired}>
          <Button
            event={alarmEvent}
            alarmPage={true}
            eventUrl={eventUrl}
            tokenExpired={tokenExpired}
            status={status}
            buttonClass="footer-button"
          />
        </Footer>
      </Flex>
    </Flex>
  );
};

const ComposedAlarm = () => {
  return (
    <PublicWrapper>
      <Alarm />
    </PublicWrapper>
  );
};
export default ComposedAlarm;
